import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, TextField, IconButton, Chip, CircularProgress, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, Alert, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon,
  Edit as EditIcon, Category as CategoryIcon,
} from '@mui/icons-material';
import { fetchCategories, fetchLocations, createCategory, updateCategory, deleteCategory } from '../../store/slices/locationSlice';
import { AppDispatch, RootState } from '../../store/store';
import { CreateLocationCategory, UpdateLocationCategory, LocationCategoryDto } from '../../types';

const ALL_ROWS_PAGE_SIZE = 100000;

const LocationCategories: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { categories, loading } = useSelector((state: RootState) => state.locations);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LocationCategoryDto | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  useEffect(() => {
    dispatch(fetchCategories({}));
  }, [dispatch]);

  // const handleSearch = (value: string) => {
  //   dispatch(fetchCategories({ searchTerm: value }));
  // };
const handleSearch = (value: string) => {
  const searchValue = value.toLowerCase();
  dispatch(fetchCategories({ searchTerm: searchValue || undefined }));
};
  const handleOpenAdd = () => {
    setEditing(null);
    setCategoryName('');
    setDescription('');
    setIsActive(true);
    setDialogOpen(true);
  };

  const handleOpenEdit = (cat: LocationCategoryDto) => {
    setEditing(cat);
    setCategoryName(cat.categoryName);
    setDescription(cat.description || '');
    setIsActive(cat.isActive);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        const data: UpdateLocationCategory = { id: editing.id, categoryName, description, isActive };
        await dispatch(updateCategory({ id: editing.id, data })).unwrap();
        setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
      } else {
        const data: CreateLocationCategory = { categoryName, description };
        await dispatch(createCategory(data)).unwrap();
        setSnackbar({ open: true, message: 'Category created successfully', severity: 'success' });
      }
      setDialogOpen(false);
      setSearchTerm(''); // Clear search box after add/edit
      dispatch(fetchCategories({})); // Fetch all categories
    } catch (err: any) {
      // Handle duplicate name error (generic backend message)
      const errorMsg = typeof err === 'string' ? err : err?.message || err?.toString();
      if (
        errorMsg &&
        (
          errorMsg.includes('duplicate') ||
          errorMsg.includes('already exists') ||
          errorMsg.includes('saving the entity changes')
        )
      ) {
        setSnackbar({ open: true, message: 'Category name already exists. Please use a different name.', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: errorMsg || 'Operation failed', severity: 'error' });
      }
    }
  };

  const handleDelete = async (category: LocationCategoryDto) => {
    try {
      const linkedLocationsResponse = await dispatch(fetchLocations({
        categoryId: category.id,
        pageNumber: 1,
        pageSize: ALL_ROWS_PAGE_SIZE,
      })).unwrap();

      const linkedLocations = linkedLocationsResponse.items;
      const linkedLocationCount = linkedLocations.length;
      const whitelistEntryCount = linkedLocations.reduce(
        (total, location) => total + (location.whitelistedEmployeeCount || 0),
        0
      );

      if (linkedLocationCount > 0 || whitelistEntryCount > 0) {
        const dependencySummary = [
          linkedLocationCount > 0 ? `${linkedLocationCount} linked location${linkedLocationCount === 1 ? '' : 's'}` : null,
          whitelistEntryCount > 0 ? `${whitelistEntryCount} whitelist entr${whitelistEntryCount === 1 ? 'y' : 'ies'}` : null,
        ].filter(Boolean).join(' and ');

        await Swal.fire({
          title: 'Cannot Delete Category',
          text: `"${category.categoryName}" cannot be deleted because it has ${dependencySummary}. Remove or reassign those dependencies first.`,
          icon: 'error',
          confirmButtonColor: '#d33',
        });
        return;
      }
    } catch (err: any) {
      const message = typeof err === 'string' ? err : err?.message || 'Failed to validate category dependencies';
      setSnackbar({ open: true, message, severity: 'error' });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete Category',
      text: `Are you sure you want to delete "${category.categoryName}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });
    if (result.isConfirmed) {
      try {
        await dispatch(deleteCategory(category.id)).unwrap();
        setSnackbar({ open: true, message: 'Category deleted', severity: 'success' });
        dispatch(fetchCategories({ searchTerm: searchTerm.toLowerCase() || undefined }));
      } catch (err: any) {
        setSnackbar({ open: true, message: err || 'Delete failed', severity: 'error' });
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <CategoryIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Location Categories
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ borderRadius: 2 ,textTransform: 'none' }}>
          Add Category
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <TextField
  placeholder="Search categories..."
  size="small"
  value={searchTerm}
  onChange={(e) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleSearch(value); // search on typing
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
  }}
  sx={{ width: 350 }}
/>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Total Records: {categories.length}
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Category Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Locations</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No categories found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((cat) => (
                  <TableRow key={cat.id} hover>
                    <TableCell><Typography fontWeight={500}>{cat.categoryName}</Typography></TableCell>
                    <TableCell>{cat.description || '-'}</TableCell>
                    <TableCell><Chip label={cat.locationCount} size="small" variant="outlined" /></TableCell>
                    <TableCell>
                      <Chip label={cat.isActive ? 'Active' : 'Inactive'} color={cat.isActive ? 'success' : 'default'} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary" onClick={() => handleOpenEdit(cat)}><EditIcon /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(cat)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Category' : 'Add New Category'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
           <TextField
              label="Category Name"
              required
              fullWidth
              value={categoryName}
              onChange={(e) => {
                let value = e.target.value;

                // 🔥 remove leading spaces
                value = value.replace(/^\s+/, "");

                // 🔥 limit to 100 chars
                if (value.length <= 100) {
                  setCategoryName(value);
                }
              }}
              placeholder="e.g. Head Office, Branch Office, Warehouse"
              error={categoryName.length > 100}
              helperText={
                categoryName.length > 100
                  ? "Maximum 100 characters allowed"
                  : `${categoryName.length}/100`
              }
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={(e) => {
                const value = e.target.value;

                // 🔥 limit to 200 chars
                if (value.length <= 200) {
                  setDescription(value);
                }
              }}
              error={description.length > 200 }
              helperText={
                description.length > 200
                  ? "Maximum 200 characters allowed"
                  : `${description.length}/200`
              }
            />
            {editing && (
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Active"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!categoryName} sx={{ textTransform: 'none' }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LocationCategories;
